import { I18n } from '../locales/translate';

export type TimeFrame = [Date | undefined, Date | undefined];
export type Duration = {
  totalInMs: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export function parseDate(value?: string | Date | null): Date | undefined {
  if (!value) {
    return;
  }
  if (typeof value !== 'string') {
    return value;
  }
  const unix = Date.parse(value);
  if (isNaN(unix)) {
    return;
  }
  return new Date(unix);
}

export function getSimpleDate(value: Date): string {
  // Important to initialize the date to a 1st day of the month (here: timestamp 0), otherwise,
  // when today is the 31st of the month and `setUTCMonth` is to a 30 days month, it will "snap"
  // to the next month instead (calling setUTCMonth with April will set to May if date was on the 31st).
  const normalizedDate = new Date(0);

  // Normalize date to avoid timezone issues
  normalizedDate.setUTCFullYear(value.getFullYear());
  normalizedDate.setUTCMonth(value.getMonth());
  normalizedDate.setUTCDate(value.getDate());
  return normalizedDate.toISOString().split('T')[0];
}

export function getMonths(format: 'short' | 'long'): string[] {
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((month) => {
    const date = new Date(0);
    date.setMonth(month);

    return Intl.DateTimeFormat(I18n.locale, { month: format }).format(date);
  });
}

export function dateBetween(value: Date, range: { start?: Date; end?: Date }): boolean {
  return (
    (range.end ? value.getTime() <= range.end.getTime() : true) &&
    (range.start ? value.getTime() >= range.start.getTime() : true)
  );
}

export function dateIsValid(date: Date | undefined): boolean {
  if (date === null || date === undefined) {
    return false;
  }
  // if any of the element of the date is NaN, the date is invalid.
  return !isNaN(date.getDay());
}

export function validDateOrUndefined(date: Date | undefined): Date | undefined {
  return dateIsValid(date) ? (date as Date) : undefined;
}

export function validTimeframeOrUndefined(
  timeframe: [Date | string | undefined | null, Date | string | undefined | null] | undefined
): TimeFrame | undefined {
  const result: TimeFrame = [undefined, undefined];
  if (timeframe) {
    result[0] = validDateOrUndefined(parseDate(timeframe[0]));
    result[1] = validDateOrUndefined(parseDate(timeframe[1]));
  }
  if (result.every((d) => d === undefined)) {
    return undefined;
  }
  return result;
}

export function beginningOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

export function endOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

export function tomorrow(): Date {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date;
}

export function atFullHour(date: Date, hour: number): Date {
  const newDate = new Date(date);
  newDate.setHours(hour, 0, 0, 0);

  return newDate;
}

export function addMsToDate(date: Date, ms: number): Date {
  return new Date(date.getTime() + ms);
}

export function addSecondsToDate(date: Date, numberOfSeconds: number): Date {
  return addMsToDate(date, numberOfSeconds * 1000);
}

export function addMinutesToDate(date: Date, numberOfMinutes: number): Date {
  return addSecondsToDate(date, numberOfMinutes * 60);
}

export function addHoursToDate(date: Date, numberOfHours: number): Date {
  return addMinutesToDate(date, numberOfHours * 60);
}

export function addDaysToDate(date: Date, numberOfDays: number): Date {
  return addHoursToDate(date, numberOfDays * 24);
}

export function getDateRangeDuration(startDate: Date, endDate: Date): Duration {
  const totalInMs = endDate.getTime() - startDate.getTime();

  const totalSeconds = Math.floor(totalInMs / 1000);
  const days = Math.floor(totalSeconds / (3600 * 24));
  const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    totalInMs,
    days,
    hours,
    minutes,
    seconds,
  };
}

export function toFullDaysRange(time: TimeFrame): TimeFrame {
  const from = time[0];
  if (!from) {
    return [undefined, undefined];
  }
  const to = time[1] || from;
  return [beginningOfDay(from), endOfDay(to)];
}

export function getCalculatedEventPublishedUntil(happeningUntil: Date): Date | null {
  if (!happeningUntil) {
    return null;
  }
  return atFullHour(addDaysToDate(happeningUntil, 1), 6);
}

export function withinTimeRange(range: TimeFrame, compareDate: Date = new Date()): boolean {
  return (!range[0] || range[0] < compareDate) && (!range[1] || compareDate < range[1]);
}

export function getLast30Days(): TimeFrame {
  const today = new Date();
  const before = new Date();
  before.setMonth(today.getMonth() - 1);
  return toFullDaysRange([before, today]);
}

export function lastMonth(): TimeFrame {
  const today = new Date();
  const end = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 0, 0, 0, 0, 0));
  const start = new Date(end);
  start.setDate(1);
  return toFullDaysRange([start, end]);
}

export function expiredDate(expiresAt?: string): boolean {
  if (expiresAt) {
    const date = new Date();
    const expiresAtDate = new Date(expiresAt);

    if (date.getTime() >= expiresAtDate.getTime()) {
      return true;
    }
  }
  return false;
}

export function earliestDate(date1: Date, date2: Date): Date {
  return new Date(Math.min(date1.getTime(), date2.getTime()));
}

// Rounds the given date to the previous 10 minutes, shifting it by 5 minutes for caching
export function roundedDatetimeToPastTenMins(date = new Date()): Date {
  const currentDate = date.getTime();
  const fiveMinsInMs = 5 * 60 * 1000;
  const tenMinsInMs = 10 * 60 * 1000;
  const rounded = Math.floor((currentDate - fiveMinsInMs) / tenMinsInMs) * tenMinsInMs + fiveMinsInMs;
  return new Date(rounded);
}
